import { useState, useEffect, useRef, useCallback } from "react";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ResearchSuggestionDropdown from "../../components/ResearchSuggestionDropdown";
import defaultProfile from "../../assets/admin.png";
import defaultResume from "../../assets/resume.png";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useSearchParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { useNavigate } from "react-router-dom";
import FollowSuggestions from "./FollowSection";
import { ThreeDot } from "react-loading-indicators";
import { getExperienceLabel } from "../jobs/Jobs";
import { LeftColumn } from './LeftColumn'
import RightColumn from "./RightColumn";
import PublicViewIframeModal from "../../components/PublicViewIframeModal";
import CodeHighlighter from "../../components/CodeHighlighter";

function Home() {
  const [articles, setArticles] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("access_token");
  const [followStatus, setFollowStatus] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [version, setVersion] = useState(0);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const contentType = searchParams.get("type");
  const searchQuery = searchParams.get("search") || "";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKey, setPageKey] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const videoRefs = useRef([]);
  const limit = 10;
  const observer = useRef();
  const location = useLocation();
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPageKey((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleLogoClick = () => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setArticles([]);
    setCurrentPage(1);
    setHasMore(true);
    setLoading(true);
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get("type") || "all";
    const searchQuery = params.get("search") || "";

    const fetchArticles = async () => {
      if (loadingMore) return; // only skip if loading more pages
      if (currentPage === 1) setLoading(true);
      else setLoadingMore(true);

      try {
        let url = "";
        if (searchQuery) {
          url = `${BASE_URL}/user/publication/search-data/?title=${encodeURIComponent(
            searchQuery
          )}&page=${currentPage}&limit=${limit}`;
        } else if (type !== "all") {
          url = `${BASE_URL}/user/publication/search-data/?type=${type}&page=${currentPage}&limit=${limit}`;
        } else {
          url = `${BASE_URL}/user/publication/home/?page=${currentPage}&limit=${limit}`;
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.REACT_APP_X_API_KEY
          },
        });

        const data = await res.json();
        if (res.ok && Array.isArray(data.results)) {
          
          setArticles((prev) =>
            currentPage === 1 ? data.results : [...prev, ...data.results]
          );

          setHasMore(currentPage < Math.ceil(data.count / limit));
          setHasMore(data.results.length === limit);
          // console.log("The type is loop:",currentPage)
        } else {
          console.error("Failed to fetch content");
        }
      } catch (err) {
        console.error("Error loading data", err.message);
        toast.error("Error loading content");
      }
      setLoading(false);
      setLoadingMore(false);
    };

    fetchArticles();
  }, [location.search, currentPage, token]);

  const updateArticleReads = (articleId, newReads) => {
    setArticles(prev => prev.map(a => a.id === articleId ? {...a, reads: newReads} : a));
  };

  const updateArticleLikes = (articleId, isLiked, newLikeCount) => {
    setArticles(prev => prev.map(a => a.id === articleId ? {...a, is_liked: isLiked, total_likes: newLikeCount} : a));
  };

  const updateArticleComments = (articleId, newCommentCount) => {
    setArticles(prev => prev.map(a => a.id === articleId ? {...a, total_comments: newCommentCount} : a));
  };

  const lastArticleRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prev) => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    const fetchFollowings = async () => {
      if (!token) return;

      let allResults = [];
      let url = `${BASE_URL}/user/follow-unfollow/follow/`;

      try {
        while (url) {
          const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}`, "x-api-key": process.env.REACT_APP_X_API_KEY } });
          allResults = allResults.concat(res.data.results);
          url = res.data.next;
        }

        const statusMap = {};
        allResults.forEach((item) => {
          const profileId = item.follow_user_profile?.id;
          if (profileId) statusMap[String(profileId)] = true;
        });

        setFollowStatus(statusMap);
        console.log("Follow status map:", statusMap);
      } catch (error) {
        console.error("Failed to fetch followings:", error);
      }
    };

    fetchFollowings();
  }, [BASE_URL, token, refresh]);

  const handleLikeChange = (postId, liked) => {
    setArticles((prev) =>
      prev.map((a) => (a.id === postId ? { ...a, is_liked: liked } : a))
    );
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const vid = entry.target;
          if (entry.intersectionRatio < 0.5 && !vid.paused) {
            vid.pause();
          }
        });
      },
      { threshold: Array.from({ length: 101 }, (_, i) => i / 100) }
    );

    videoRefs.current.forEach((vid) => {
      if (vid) observer.observe(vid);
    });

    return () => {
      videoRefs.current.forEach((vid) => {
        if (vid) observer.unobserve(vid);
      });
    };
  }, [articles]);

  useEffect(() => {

  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY,
          },
        });

        const data = await res.json();
        // console.log("The profile data is:", data)
        if (res.ok && data?.data?.user) {
          setCurrentUserId(data.data.id);
          setCurrentUser(data.data);
        } else {
          console.error("Failed to fetch user ID.");
        }
      } catch (err) {
        console.error("Error fetching user profile:", err.message);
      }
    };

    fetchProfile();
  }, [BASE_URL, token]);

  const renderMedia = (article, index) => {
    switch (article.type) {
      case "video":
        return article.video ? (
          <div className="media-wrapper">
            <video
              controls
              className="w-full h-[350px] rounded-lg object-cover border border-gray-100"
              ref={(el) => (videoRefs.current[index] = el)}
              onClick={(e) => e.preventDefault()}
              onPlay={(e) => e.stopPropagation()}
              onPause={(e) => e.stopPropagation()}
            >
              <source src={article.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ) : null;

      case "image":
        return article.image ? (
          <div className="media-wrapper">
            <img src={article.image} alt="Posted" className="w-full h-[350px] rounded-lg object-cover border border-gray-100" />
          </div>
        ) : null;

      case "publication":
        if (!article.upload_document) {
          return (
            <div className="media-wrapper">
              <em>No document uploaded</em>
            </div>
          );
        }

        const fileName = article.upload_document.split("/").pop();
        const ext = fileName.split(".").pop().toLowerCase();

        let fileTypeLabel = "Document";
        if (ext === "pdf") fileTypeLabel = "PDF Document";
        else if (["doc", "docx"].includes(ext)) fileTypeLabel = "Word Document";
        else if (["xls", "xlsx"].includes(ext))
          fileTypeLabel = "Excel Spreadsheet";
        else if (["ppt", "pptx"].includes(ext))
          fileTypeLabel = "PowerPoint Presentation";
        else if (["txt"].includes(ext)) fileTypeLabel = "Text File";

        return (
          <>
            <div
              className="w-full gap-2.5 border border-gray-100 p-2.5 rounded-lg cursor-pointer max-w-[800px]"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
                window.history.pushState({}, "", `/post/${article.id}?type=publication`);
                setModalContent({
                  url: article.upload_document,
                  title: article.title,
                  description: article.description || article.abstraction,
                  type: "publication",
                  userId: article.user,
                  postId: article.id,
                  is_Liked: article.is_liked === true,
                  likeCount: article.total_likes,
                  commentCount: article.total_comments,
                });
              }}
            >
              <div className="card_home_inner">
                {/* File Preview */}
                {["png", "jpg", "jpeg", "gif", "webp"].includes(ext) ? (
                  <img
                    src={article.upload_document}
                    alt={fileName}
                    className="w-full object-cover rounded-md"
                  />
                ) : ext === "pdf" ? (
                  <embed
                    src={`${article.upload_document}#toolbar=0&navpanes=0&scrollbar=0`}
                    type="application/pdf"
                    className="w-full h-[200px] rounded-md"
                  />
                ) : (
                  <iframe
                    src={`https://docs.google.com/gview?url=${article.upload_document}&embedded=true`}
                    className="w-full h-[200px] border-0"
                    title="Document Preview"
                  />
                )}

                {/* File Info Below */}
                <div className="mt-[8px]">
                  <div className="font-bold text-sm overflow-hidden overflow-ellipsis whitespace-nowrap">
                    {fileName}
                  </div>
                  <div className="text-xs text-gray-500">{fileTypeLabel}</div>
                </div>
              </div>
            </div>
            
            {isModalOpen && modalContent && (
              <PublicViewIframeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}  // Changed from setIsModalOpen to onClose
                onReadsUpdate={updateArticleReads}
                {...modalContent}
              />
            )}
          </>

        );

      case "code":
        return (
          <div className="media-wrapper flex justify-center">
            <div className="relative w-full max-w-[800px]">
              <pre className="w-full max-h-[600px] h-[350px]  rounded-lg border border-gray-100 font-mono text-sm bg-gray-700 text-gray-100 overflow-y-auto whitespace-pre-wrap break-words">
                <CodeHighlighter codeHtml={article.code} padding='p-4' />
              </pre>
            </div>
          </div>
        );


      case "post":
        return (
          <div className="box_inner_design">
            <div className="box_top">
              <h3>{article.title}</h3>
              <p>
                <strong>Content:</strong> {article.content}
              </p>
            </div>
            {article.post_type === "job" && (
              <div className="inbox_list">
                <p>
                  <strong>Job Title:</strong> {article.job_title}
                </p>
                <p>
                  <strong>Company:</strong> {article.company_name}
                </p>
                <p>
                  <strong>Location:</strong> {article.location}
                </p>
                <p>
                  <strong>Salary:</strong>{" "}
                  {article.salary_min && article.salary_max
                    ? `${article.salary_min} - ${article.salary_max} ${article.currency}`
                    : "Not specified"}
                </p>

                <p>
                  <strong>Skills:</strong>{" "}
                  {Array.isArray(article.skills_required) &&
                    article.skills_required.length
                    ? article.skills_required.join(", ")
                    : article.skills_required || "Not specified"}
                </p>
                <p>
                  <strong>Remote:</strong>{" "}
                  {article.remote_allowed ? "Yes" : "No"}
                </p>
                <p>
                  <strong>Qualifications:</strong> {article.qualifications}
                </p>
                <p>
                  <strong>Experience:</strong>{" "}
                  {getExperienceLabel(article.experience_level)}
                </p>
                <p>
                  <strong>Email:</strong> {article.application_email}
                </p>
                <p>
                  <strong>Link:</strong> {article.application_url}
                </p>
              </div>
            )}
          </div>
        );

      default:
        if (article.upload_document?.endsWith(".pdf")) {
          return (
            <div className="media-wrapper">
              <iframe
                src={article.upload_document}
                title="PDF"
                className="w-full h-[350px] rounded-lg object-cover border border-gray-100"
              />
            </div>
          );
        } else if (article.upload_document) {
          return (
            <div className="media-wrapper">
              <img
                src={article.upload_document}
                alt="Uploaded document"
                className="w-full h-[350px] rounded-lg object-cover border border-gray-100"
              />
            </div>
          );
        } else {
          return (
            <div className="media-wrapper">
              <div className="w-full max-w-[800px] max-h-[600px] h-[350px] p-3 rounded-lg object-cover border border-gray-100 font-mono text-sm whitespace-pre-wrap break-words">
                <em>No media available</em>
              </div>
            </div>
          );
        }
    }
  };
  function getRelativeTime(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    if (weeks < 4) return `${weeks}w`;
    return `${months}mo`;
  }
  const handleFollowToggle = async (userIdOfPost) => {
    const token = localStorage.getItem("access_token");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-api-key": process.env.REACT_APP_X_API_KEY
    };
    const stringUserId = String(userIdOfPost);
    try {
      setLoading(true);
      const isAlreadyFollowing = followStatus[stringUserId];
      if (isAlreadyFollowing) {
        const response = await axios.delete(
          `${BASE_URL}/user/follow-unfollow/follow/`,
          {
            headers,
            data: { id: userIdOfPost },
          }
        );

        if (
          response.status === 204 ||
          response.data?.message === "Successfully unfollowed."
        ) {
          setFollowStatus((prev) => ({
            ...prev,
            [stringUserId]: false,
          }));
          setRefresh((prev) => !prev);
          toast.success("Unfollowed successfully");
          setVersion((v) => v + 1);
        }
      } else {
        const response = await axios.post(
          `${BASE_URL}/user/follow-unfollow/follow/`,
          { id: userIdOfPost },
          { headers }
        );
        if (response.data.message === "Successfully followed") {
          setFollowStatus((prev) => ({
            ...prev,
            [stringUserId]: true,
          }));
          toast.success("Followed successfully");
          setVersion((v) => v + 1);
        } else if (
          response.data.error === "You are already following this user."
        ) {
          toast.info("Already following");
        }
      }
    } catch (error) {
      toast.error("Something went wrong");
      console.error("Follow/Unfollow Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const otherArticles = articles?.filter(
    (article) => article.type !== "post" && article.type !== "job"
  );

  const displayArticles =
    searchQuery || contentType === "post" || contentType === "job" || contentType === "normal"
      ? articles
      : otherArticles;

  const renderArticleItem = (article, index) => {
    const isLastArticle = index === articles.length - 1;
    const userProfile = article.user_profile || {};
    // console.log('users profiles', userProfile)
    const userIdOfPost =
      userProfile.id || userProfile.user || userProfile.user_id || null;
    const userName = userProfile.name || "Unknown";
    const profilePic = userProfile?.profile_picture || defaultProfile;
    const articleDate = getRelativeTime(article.created_at);
    const shouldShowFollow =
      userIdOfPost &&
      currentUserId &&
      String(userIdOfPost) !== String(currentUserId);
    const postType =
      article.type === "post"
        ? article.post_type || "post"
        : article.type || "publication";
    const postUrl = `/post/${article.id}?type=${postType}`;
    const userProfileLink =
      String(userIdOfPost) === String(currentUserId)
        ? "/profile-view"
        : `/user-profile/${userIdOfPost}`;
    const online_status = userProfile?.online_status;
    const last_seen_display = userProfile?.last_seen_display;
    return (
      <div className="w-full" key={article.id} ref={isLastArticle ? lastArticleRef : null}>
        <ResearchSuggestionDropdown
          online_status={online_status}
          last_seen_display={last_seen_display}
          profileImage={profilePic}
          userName={userName}
          userProfileLink={userProfileLink}
          postId={article.id}
          disableNavigation={article.type === "publication"}
          type={article.type}
          post_type={article.post_type}
          heading={article.title || postType.toUpperCase()}
          articleDate={articleDate}
          reads={article.reads || 0}
          author={article.author}
          showFollow={shouldShowFollow}
          onFollowToggle={() => handleFollowToggle(userIdOfPost)}
          isFollowing={followStatus[String(userIdOfPost)]}
          creations={article.creations || 0}
          journal={article.publication_type || null}
          ButtonText="View"
          currentUserId={currentUserId}
          authorId={userIdOfPost}
          currentUser={currentUser}
          resumeImage={defaultResume}
          researcherCount={article.researcherCount || 1}
          mediaPreview={renderMedia(article, index)}
          latest_comment={article.latest_comment}
          commentCount={article.total_comments}
          showBackButton={false}
          likeCount={article.total_likes}
          onLikeChange={handleLikeChange}
          is_Liked={article.is_liked}
          onProfileClick={() => {
            if (String(userIdOfPost) === String(currentUserId)) {
              navigate("/profile-view", {
                state: {
                  isOwnProfile: true,
                  profileData: userProfile,
                },
              });
            } else {
              navigate(`/user-profile/${userIdOfPost}`, {
                state: {
                  isOwnProfile: false,
                  profileData: userProfile,
                },
              });
            }
          }}
          bodyDescription={
            article.type === "image" || article.type === "video"
              ? article.description
              : article.type === "publication" || article.type === "code"
                ? article.title
                : null
          }
          postUrl={postUrl}
          jobData={article.post_type === "job" ? article : null}
          onReadsUpdate={updateArticleReads}
          onLikesUpdate={updateArticleLikes}
          onCommentsUpdate={updateArticleComments}
        />
      </div>
    );
  };
  return (
    < >
      <Header onLogoClick={handleLogoClick} />
      <div>
        <div>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <ClipLoader size={50} color="#123abc" />
            </div>
          ) : articles?.length === 0 ? (
            <FollowSuggestions
              followStatus={followStatus}
              setFollowStatus={setFollowStatus}
              currentUserId={currentUserId}
              onFollowToggle={handleFollowToggle}
            />
          ) : (
            <>

              <div className="container mx-auto md:px-4 mt-6 mb-4">
                <div className="flex relative">

                  {/* LEFT - Absolutely positioned for proper sticky behavior */}
                  <div className="hidden md:block w-1/4 px-2">
                    <div className="sticky top-[80px] max-h-[calc(100vh-80px)] overflow-y-auto scrollbar-hidden">
                      <LeftColumn />
                    </div>
                  </div>

                  {/* CENTER - Main content with normal flow */}
                  <div className="w-full md:w-2/4 md:px-2">
                    {displayArticles.map((article, index) =>
                      renderArticleItem(article, index)
                    )}
                  </div>

                  {/* RIGHT - Absolutely positioned for proper sticky behavior */}
                  <div className="hidden md:block w-1/4 px-2">
                    <div className="sticky top-[80px]  max-h-[calc(100vh-80px)] overflow-y-auto scrollbar-hidden">
                      <RightColumn contentType={contentType} searchQuery={searchQuery} />
                    </div>
                  </div>

                </div>
              </div>






              {loadingMore && (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <ThreeDot size={30} color="#123abc" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Footer removed on infinite scroll home page; left sidebar has important links. */}
    </>
  );
}
export default Home;
